import { GetStaticPropsContext } from 'next';

import { DEFAULT_CHINA_LOCALE } from '../../common/constants';
import { transformResponseUrls } from './transform-response-urls';

/**
 * Resolves the locale for URL transformation based on context.
 * Returns undefined if locale matches default (except for China).
 */
const getTransformLocale = (context: GetStaticPropsContext): string | undefined => {
  const { locale, defaultLocale } = context;
  return locale === defaultLocale && locale?.toLowerCase() !== DEFAULT_CHINA_LOCALE.toLowerCase()
    ? undefined
    : locale;
};

/**
 * Transforms URL properties in the given props object according to the locale
 * derived from the provided Next.js static props context.
 *
 * If the current locale matches the default locale (US and CN)
 * no locale-based URL transformation is applied. Otherwise, URLs are rewritten
 * to include the appropriate locale prefix.
 *
 * @template T - The shape of the props object, must be a plain record.
 * @param props - The props object whose URL values should be transformed.
 * @param context - The Next.js `GetStaticPropsContext` providing locale information.
 * @returns A new props object of the same type with transformed URL values.
 */
export const transformPropsWithLocale = <T extends Record<string, unknown>>(
  props: T,
  context: GetStaticPropsContext
): T => transformResponseUrls(props, getTransformLocale(context));
